/**
 * Word/line/character statistics for editor content.
 *
 * Character counts are deliberately split into two figures:
 *
 *  - `charsWithMarkup` — the raw source length, every character included.
 *    This is what counts against any length limit and what a reader of
 *    the *source* sees.
 *  - `charsWithoutMarkup` — an approximation of how much prose is really
 *    there: markup delimiters, URLs, and control characters stripped.
 *    This is the figure that answers "how long is this document, as
 *    text" — the thing word processors report.
 *
 * "Words" are counted as whitespace-separated runs of characters that
 * contain at least one alphanumeric character — so punctuation and bare
 * markup markers don't inflate the count.
 *
 * These are computed on demand with simple, O(n) string operations —
 * cheap enough to run on every keystroke without debouncing.
 */

export interface EditorStats {
  lines: number;
  words: number;
  charsWithMarkup: number;
  charsWithoutMarkup: number;
}

/** Strips lightweight-markup so the remaining string approximates readable prose. */
function stripMarkup(text: string): string {
  return (
    text
      // URLs — a link's destination isn't prose. Count its label instead.
      .replace(/https?:\/\/\S+/g, ' ')
      .replace(/wikistar-media:[0-9a-f]{64}/g, ' ')
      // Code fences and their language tags.
      .replace(/```\w*/g, ' ')
      // Inline-code and markup delimiters.
      .replace(/[{}\[\]`*_~^|=!<>#$]/g, '')
      // Djot/AsciiDoc/Markdown heading and list markers at line start.
      .replace(/^\s*(#{1,6}\s+|={1,6}\s+|[-*+]\s+|\d+\.\s+|>\s+)/gm, '')
  );
}

export function computeEditorStats(content: string): EditorStats {
  const lines = content === '' ? 0 : content.split('\n').length;

  const words = content
    .split(/\s+/)
    .filter((token) => /[A-Za-z0-9]/.test(token))
    .length;

  const charsWithMarkup = content.length;
  const charsWithoutMarkup = stripMarkup(content).replace(/\s+/g, ' ').trim().length;

  return { lines, words, charsWithMarkup, charsWithoutMarkup };
}
